package gr.tech.GRAttestation.sections.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.modals.ConfirmModal
import gr.tech.GRAttestation.components.modals.ModalOverlay
import gr.tech.GRAttestation.components.modals.StoreUserAddModal
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.sections.tables.AdminStoreInvitesTable
import gr.tech.GRAttestation.util.Constants
import gr.tech.models.storeusers.StoreUsersInviteDTO
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

/*
    StoreInvitesPanel
    -----------------
    Pure UI composable — no data fetching or API calls.
    The parent page owns all state and provides data + callbacks.

    Use in store-scoped contexts (Details.kt) or all-stores contexts (users/Index.kt)
    by wiring the callbacks to the appropriate API functions.
*/
@Composable
fun AdminStoreInvitesPanel(
    alertManager: AlertManager,
    invites: List<StoreUsersInviteDTO>,
    totalCount: Int,
    currentPage: Int,
    basicFilters: BasicTableFilters,
    lockedStoreId: Int? = null,
    onPageChange: (Int) -> Unit,
    onFiltersChange: (BasicTableFilters) -> Unit,
    onInviteCreated: () -> Unit,
    onCancelInvite: (StoreUsersInviteDTO) -> Unit,
    filterId: String = "storeInvitesPanelFilters",
) {
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (totalCount + pageSize - 1) / pageSize

    var showAddInviteModal by remember { mutableStateOf(false) }
    var currentCancelInvite: StoreUsersInviteDTO? by remember { mutableStateOf(null) }

    var selectedDateColumnLabel by remember { mutableStateOf("Sent At") }

    // New Invite
    ModalOverlay(
        isOpen = showAddInviteModal,
        onClose = { showAddInviteModal = false },
        content = {
            StoreUserAddModal(
                lockedStoreId = lockedStoreId,
                alertManager = alertManager,
                onClose = { showAddInviteModal = false },
                onInviteCreated = { onInviteCreated() }
            )
        }
    )

    // Cancel Invite
    ModalOverlay(
        isOpen = currentCancelInvite != null,
        onClose = { currentCancelInvite = null },
        content = {
            currentCancelInvite?.let { invite ->
                ConfirmModal(
                    message = "Are you sure you want to cancel the registration invite to ${invite.email}?",
                    confirmText = "Cancel Invite",
                    onConfirm = {
                        onCancelInvite(invite)
                        currentCancelInvite = null
                    },
                    onClose = { currentCancelInvite = null }
                )
            }
        }
    )

    Column(modifier = Modifier.fillMaxWidth()) {

        // Panel header row
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {}
                    .toAttrs {
                        attr("data-bs-toggle", "collapse")
                        attr("data-bs-target", "#$filterId")
                    }
            ) {
                FaFilter(modifier = Modifier.margin(right = 8.px))
                Text("Filters")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {
//                        formError = ""
                        showAddInviteModal = true
                    }
                    .toAttrs()
            ) {
                FaPlus(modifier = Modifier.margin(right = 8.px))
                Text("New Invite")
            }

            Spacer()

            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { onPageChange(it) }
            )
        }

        // Collapsable Filters
        Column(
            modifier = Modifier.id(filterId).classNames("collapse")
                .margin(top = 8.px, bottom = 16.px)
                .padding(8.px)
                .backgroundColor(Colors.WhiteSmoke)
                .borderRadius(8.px)
                .fillMaxWidth()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .toAttrs { attr("data-bs-toggle", "dropdown") }
                    ) { Text(selectedDateColumnLabel) }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick {
                                    onFiltersChange(basicFilters.copy(dateColumn = "createdAt"))
                                    selectedDateColumnLabel = "Sent At"
                                }.toAttrs()
                            ) { Text("Sent At") }
                        }
                    }
                }

                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isAscending = true)) }
                            .toAttrs()
                    ) { Text("ASCENDING") }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isAscending = false)) }
                            .toAttrs()
                    ) { Text("DESCENDING") }
                }

                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isBefore = true)) }
                            .toAttrs()
                    ) { Text("BEFORE") }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isBefore = false)) }
                            .toAttrs()
                    ) { Text("AFTER") }
                }

                Input(
                    type = InputType.Date,
                    attrs = Modifier.classNames("form-control").width(200.px)
                        .toAttrs {
                            attr("value", basicFilters.dateString)
                            onChange { onFiltersChange(basicFilters.copy(dateString = it.value)) }
                        }
                )

                Spacer()

                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .onClick {
                            onFiltersChange(BasicTableFilters())
                            selectedDateColumnLabel = "Sent At"
                        }
                        .toAttrs()
                ) {
                    FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                    Text("Reset")
                }
            }
        }

        AdminStoreInvitesTable(
            invites = invites,
            onCancel = { invite ->
                currentCancelInvite = invite
            }
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { onPageChange(it) }
            )
        }
    }
}
